import { IconCaretDownSmall } from "@suse-o11y/icons";
import { prop } from "@suse-o11y/prop";
import { Tooltip, TooltipBoundary, TooltipTrigger } from "@suse-o11y/tooltip";
import { useHandler } from "@suse-o11y/use-handler";
import React from "react";
import { useHotkeys } from "react-hotkeys-hook";

import { DropdownMenu } from "~/components/dropdown-menu";
import { DropdownMenuContent } from "~/components/dropdown-menu/content";
import { DropdownMenuHotkey } from "~/components/dropdown-menu/hotkey";
import { DropdownMenuItem } from "~/components/dropdown-menu/item";
import { DropdownMenuTrigger } from "~/components/dropdown-menu/trigger";

import { RESET_VIEW_SHORTCUT, SAVE_AS_VIEW_SHORTCUT, SAVE_VIEW_SHORTCUT } from "../../../app/Shortcuts";

type TriggerRenderer<T> = (value: T, children: React.ReactNode) => React.ReactNode;

export interface SaveAsButtonAction<T = unknown> {
	/**
	 * A value used to determine when an action is visible.
	 * When truthy - the action is visible and the value passed to the {@link onSelect},
	 * otherwise the action is not hidden.
	 */
	enabler: false | null | undefined | T;

	/**
	 * Whether the action's side effect is active.
	 * When any of the secondary actions is active, the dropdown menu stays open until the action is no longer active.
	 */
	active?: boolean;

	title: string;
	isPrimary?: boolean;
	onSelect: (value: T) => void;
	trigger?: TriggerRenderer<T>;
}

function renderTrigger(render: undefined | TriggerRenderer<unknown>, value: unknown, children: React.ReactNode) {
	if (render == null) {
		return children;
	}

	return render(value, children);
}

export interface SaveButtonProps {
	save: SaveAsButtonAction;
	saveAs: SaveAsButtonAction;
	reset: SaveAsButtonAction;
	rest?: ReadonlyArray<SaveAsButtonAction>;
	isDropdownOpen: boolean;
	onDropdownOpen: (open: boolean) => void;
}

export const SaveButton: React.FC<SaveButtonProps> = ({
	save,
	saveAs,
	reset,
	rest = [],
	isDropdownOpen,
	onDropdownOpen,
}) => {
	const onDropdownOpenRef = React.useRef<VoidFunction>();

	const [primaryAction, ...secondaryActions] = [
		{ ...save, hotkey: SAVE_VIEW_SHORTCUT },
		{ ...saveAs, hotkey: SAVE_AS_VIEW_SHORTCUT },
		{ ...reset, hotkey: RESET_VIEW_SHORTCUT },
		...rest.map((action) => ({ ...action, hotkey: null })),
	].filter(prop("enabler"));

	const isActiveOrOpen = isDropdownOpen || secondaryActions.some(prop("active"));

	const onActionHotkey = (action: SaveAsButtonAction) => {
		if (isActiveOrOpen || action.trigger == null || action.title === primaryAction?.title) {
			action.onSelect(action.enabler);
		} else {
			/**
			 * Open the dropdown in case the {@link action} has a trigger in the dropdown menu.
			 */
			onDropdownOpen(true);
			/**
			 * Due to race conditions, schedule the {@link action.onSelect} to be called after the dropdown is opened.
			 * Otherwise, triggered action registered before the dropdown which brakes the Escape key queue.
			 */
			onDropdownOpenRef.current = () => action.onSelect(action.enabler);
		}
	};

	useHotkeys(
		SAVE_VIEW_SHORTCUT,
		useHandler(() => onActionHotkey(save)),
		{ enabled: Boolean(save.enabler) },
	);

	useHotkeys(
		SAVE_AS_VIEW_SHORTCUT,
		useHandler(() => onActionHotkey(saveAs)),
		{ enabled: Boolean(saveAs.enabler) },
	);

	useHotkeys(
		RESET_VIEW_SHORTCUT,
		useHandler(() => onActionHotkey(reset)),
		{ enabled: Boolean(reset.enabler) },
	);

	React.useEffect(() => {
		if (isActiveOrOpen) {
			onDropdownOpenRef.current?.();
			onDropdownOpenRef.current = undefined;
		}
	}, [isActiveOrOpen]);

	if (primaryAction == null) {
		return null;
	}

	return (
		<div className="join join-space-px group" data-variant={primaryAction.isPrimary ? "primary" : "default"}>
			<TooltipBoundary hide={primaryAction.hotkey == null}>
				<TooltipTrigger asChild>
					{renderTrigger(
						primaryAction.trigger,
						primaryAction.enabler,

						<button
							className="join-item top-nav-button group-data-[variant=primary]:top-nav-button-highlighted"
							onClick={
								primaryAction.trigger == null
									? () => {
											primaryAction.onSelect(primaryAction.enabler);
										}
									: undefined
							}
							type="button"
						>
							{primaryAction.title}
						</button>,
					)}
				</TooltipTrigger>

				<Tooltip className="tooltip/tooltip">
					<code className="code text-core-text-neutral-primary">{primaryAction.hotkey}</code>
				</Tooltip>
			</TooltipBoundary>

			{secondaryActions.length > 0 && (
				<DropdownMenu onOpenChange={onDropdownOpen} open={isActiveOrOpen}>
					<DropdownMenuTrigger
						aria-label="Show more actions"
						asChild={false}
						className="join-item top-nav-button top-nav-button-skinny group-data-[variant=primary]:top-nav-button-highlighted"
						type="button"
					>
						<IconCaretDownSmall width="7" />
					</DropdownMenuTrigger>

					<DropdownMenuContent align="end">
						{[primaryAction, ...secondaryActions].map((action, index) => (
							<React.Fragment key={action.title}>
								{renderTrigger(
									// do not render primary action trigger - it is already rendered as the main button
									index === 0 ? undefined : action.trigger,
									action.enabler,

									<DropdownMenuItem
										onSelect={(event) => {
											if (index === 0 || action.trigger == null) {
												action.onSelect(action.enabler);
											} else {
												event.preventDefault();
											}
										}}
									>
										{action.title}

										{action.hotkey != null && <DropdownMenuHotkey>{action.hotkey}</DropdownMenuHotkey>}
									</DropdownMenuItem>,
								)}
							</React.Fragment>
						))}
					</DropdownMenuContent>
				</DropdownMenu>
			)}
		</div>
	);
};
